import { useState } from 'react';
import { CheckCircle2, Loader2, Mail } from 'lucide-react';

/**
 * "Email me a summary of this conversation" — the form the visitor fills in to
 * get a recap of their own chat.
 *
 * Rendered inside the transcript for the same reason ContactFormCard is: the
 * composer stays available, so a visitor who wants to ask one more thing
 * before handing over their address does not have to dismiss anything first.
 *
 * Only name and email are asked for. Which conversation to summarise is
 * decided by the backend from the caller's own connection, so there is nothing
 * else the visitor could usefully tell us here — and every extra field is one
 * more reason to abandon the form.
 */

export function ChatSummaryCard({
  theme,
  primaryColor,
  defaultName,
  defaultEmail,
  onSubmit,
}: {
  theme: 'light' | 'dark';
  primaryColor: string;
  /** Prefilled when the visitor has already told us, so they need not retype it. */
  defaultName?: string;
  defaultEmail?: string;
  onSubmit: (values: { name: string; email: string }) => Promise<{ success: boolean; message?: string; error?: string }>;
}) {
  const dark = theme === 'dark';
  const [name, setName] = useState(defaultName ?? '');
  const [email, setEmail] = useState(defaultEmail ?? '');
  const [errors, setErrors] = useState<{ name?: string; email?: string }>({});
  const [focused, setFocused] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const [doneMessage, setDoneMessage] = useState('');
  // A refusal from the backend (no chats yet, rate limited) is shown in the
  // form rather than thrown away, because every one of them is something the
  // visitor can act on.
  const [failure, setFailure] = useState('');

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (saving || done) return;

    const found: { name?: string; email?: string } = {};
    if (!name.trim()) found.name = 'Name is required.';
    if (!email.trim()) {
      found.email = 'Email is required.';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      found.email = 'Enter a valid email address.';
    }
    setErrors(found);
    if (Object.keys(found).length > 0) return;

    setFailure('');
    setSaving(true);
    try {
      const result = await onSubmit({ name: name.trim(), email: email.trim() });
      if (result.success) {
        setDoneMessage(result.message || `Your summary is on its way to ${email.trim()}.`);
        setDone(true);
      } else {
        setFailure(result.error || 'We could not send your summary. Please try again.');
      }
    } finally {
      setSaving(false);
    }
  };

  const border = dark ? 'vw-border-gray-700' : 'vw-border-gray-200';
  const surface = dark ? 'vw-bg-gray-800' : 'vw-bg-white';
  const labelColor = dark ? 'vw-text-gray-200' : 'vw-text-gray-700';
  const mutedColor = dark ? 'vw-text-gray-400' : 'vw-text-gray-500';
  const inputClass = `vw-w-full vw-px-3 vw-py-2 vw-rounded-[12px] vw-border vw-outline-none vw-transition-all vw-text-sm ${
    dark
      ? 'vw-bg-gray-700 vw-border-gray-600 vw-text-white vw-placeholder-gray-400'
      : 'vw-bg-gray-50 vw-border-gray-200 vw-text-gray-900 vw-placeholder-gray-400'
  }`;

  if (done) {
    // A receipt, not a form — re-rendering the inputs after a successful send
    // invites a duplicate request and looks like nothing happened.
    return (
      <div
        className={`vw-mt-2 vw-flex vw-items-center vw-gap-2 vw-rounded-[17px] vw-border ${border} ${surface} vw-px-4 vw-py-3`}
      >
        <CheckCircle2 className="vw-h-4 vw-w-4 vw-shrink-0" style={{ color: primaryColor }} />
        <span className={`vw-text-sm ${labelColor}`}>{doneMessage}</span>
      </div>
    );
  }

  const ringFor = (field: string) =>
    focused === field
      ? { borderColor: primaryColor, boxShadow: `0 0 0 1px ${primaryColor}` }
      : undefined;

  return (
    <form
      onSubmit={submit}
      className={`vw-mt-2 vw-space-y-3 vw-rounded-[17px] vw-border ${border} ${surface} vw-p-4`}
    >
      <div className="vw-flex vw-items-center vw-gap-2">
        <Mail className="vw-h-4 vw-w-4 vw-shrink-0" style={{ color: primaryColor }} />
        <span className={`vw-text-sm vw-font-medium ${labelColor}`}>
          Email me a summary of this conversation
        </span>
      </div>
      <p className={`vw-text-xs ${mutedColor}`}>
        We'll send a recap of what we covered to your inbox.
      </p>

      <div>
        <label className={`vw-block vw-text-sm vw-font-medium vw-mb-1 ${labelColor}`}>
          Name<span className="vw-text-red-500 vw-ml-1">*</span>
        </label>
        <input
          type="text"
          value={name}
          placeholder="Your name"
          disabled={saving}
          style={ringFor('name')}
          onFocus={() => setFocused('name')}
          onBlur={() => setFocused(null)}
          onChange={(e) => setName(e.target.value)}
          className={inputClass}
        />
        {errors.name && <p className="vw-mt-1 vw-text-xs vw-text-red-500">{errors.name}</p>}
      </div>

      <div>
        <label className={`vw-block vw-text-sm vw-font-medium vw-mb-1 ${labelColor}`}>
          Email<span className="vw-text-red-500 vw-ml-1">*</span>
        </label>
        <input
          type="email"
          value={email}
          placeholder="you@company.com"
          disabled={saving}
          style={ringFor('email')}
          onFocus={() => setFocused('email')}
          onBlur={() => setFocused(null)}
          onChange={(e) => setEmail(e.target.value)}
          className={inputClass}
        />
        {errors.email && <p className="vw-mt-1 vw-text-xs vw-text-red-500">{errors.email}</p>}
      </div>

      {failure && <p className="vw-text-xs vw-text-red-500">{failure}</p>}

      <button
        type="submit"
        disabled={saving}
        style={{ backgroundColor: primaryColor }}
        className="vw-w-full vw-flex vw-items-center vw-justify-center vw-gap-2 vw-rounded-[12px] vw-px-4 vw-py-2.5 vw-text-sm vw-font-medium vw-text-white vw-transition-all hover:vw-opacity-90 disabled:vw-opacity-50"
      >
        {saving && <Loader2 className="vw-h-4 vw-w-4 vw-animate-spin" />}
        {saving ? 'Sending…' : 'Send me the summary'}
      </button>
    </form>
  );
}
